import dayjs from "dayjs";
import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Space,
  Spin,
  message,
  Drawer,
  Descriptions,
  Divider,
  Dropdown,
  DatePicker,
} from "antd";
import {
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  RightOutlined,
  DownOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import {
  adminBookingService,
  adminRoomService,
  frontDeskService,
  offersService,
  reservationPrintService,
} from "../../services/adminService";
import { formatLkr } from "../../utils/currency";
import { socketService } from "../../services/socketService";
import { ViewBookingModal } from "./components/ViewBookingModal";
import { AllocateModal } from "./components/AllocateModal";
import { CheckInModal } from "../frontdesk/components/CheckInModal";
import { EditBookingModal } from "./components/EditBookingModal";
import { CheckOutModal } from "../frontdesk/components/CheckOutModal";
import { RoomCheckOutInspectionModal } from "./components/RoomCheckOutInspectionModal";
import { getBookingColumns } from "./columns";
import "./Bookings.css";

const { RangePicker } = DatePicker;

export interface Booking {
  id: string;
  guest_name: string;
  email: string;
  phone: string;
  check_in_date: string;
  check_out_date: string;
  room_type_id: string;
  number_of_guests: number;
  special_requests?: string;
  total_price: number;
  status: "pending" | "confirmed" | "checked_in" | "checked_out" | "cancelled" | "no_show" | "blocked" | "reserved";
  payment_status?: "pending" | "completed" | "failed";
  confirmation_number?: string;
  created_at: string;
  room_id?: string;
  room_number?: string;
  roomType?: any;
  extra_beds?: number;
  payment_plan?: "deposit" | "full";
  reservation_source?: string;
  quantity?: number;
  ac_status?: "ac" | "non_ac" | "both";
  room_package_id?: string | null;
  vat_rate?: number;
}

const Bookings: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialSearch = searchParams.get("search") || "";

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState(initialSearch);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined,
  );
  const [dateRangeFilter, setDateRangeFilter] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | null
  >(null);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);

  const [checkOutModalVisible, setCheckOutModalVisible] = useState(false);
  const [roomCheckModalVisible, setRoomCheckModalVisible] = useState(false);
  const [editActiveTab, setEditActiveTab] = useState("1");
  const [selectedCheckOutBooking, setSelectedCheckOutBooking] =
    useState<Booking | null>(null);
  const [selectedRoomCheckBooking, setSelectedRoomCheckBooking] =
    useState<Booking | null>(null);

  const [checkOutForm] = Form.useForm();
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [roomAvailabilities, setRoomAvailabilities] = useState<
    Record<string, any>
  >({});

  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [printDateFrom, setPrintDateFrom] = useState<string>(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [printDateTo, setPrintDateTo] = useState<string>(dayjs().endOf("month").format("YYYY-MM-DD"));
  const [printStatusFilter, setPrintStatusFilter] = useState<string | undefined>(undefined);

  const handlePrintReservationList = useCallback(async () => {
    setPrintLoading(true);
    try {
      const blob = await reservationPrintService.getReservationListPdf(printDateFrom, printDateTo, printStatusFilter);
      reservationPrintService.downloadPdf(blob, `reservation-list-${printDateFrom}-to-${printDateTo}.pdf`);
      message.success("Reservation list downloaded");
      setPrintModalOpen(false);
    } catch (err) {
      message.error("Failed to generate PDF");
    } finally {
      setPrintLoading(false);
    }
  }, [printDateFrom, printDateTo, printStatusFilter]);

  const editCheckInDate = Form.useWatch("check_in_date", editForm);
  const editCheckOutDate = Form.useWatch("check_out_date", editForm);
  const editRoomsList = Form.useWatch("rooms", editForm) || [];

  useEffect(() => {
    if (!editModalVisible) return;

    const uniqueRoomTypeIds = Array.from(
      new Set(editRoomsList.map((r: any) => r?.room_type_id).filter(Boolean)),
    ) as string[];

    if (
      uniqueRoomTypeIds.length > 0 &&
      editCheckInDate &&
      editCheckOutDate
    ) {
      const fetchAvailability = async () => {
        const newAvailabilities: Record<string, any> = {};
        await Promise.all(
          uniqueRoomTypeIds.map(async (rtId) => {
            try {
              const res = await adminRoomService.getRoomTypeAvailability(rtId, {
                checkInDate: editCheckInDate.format("YYYY-MM-DD"),
                checkOutDate: editCheckOutDate.format("YYYY-MM-DD"),
              });
              newAvailabilities[rtId] = res.data || null;
            } catch (err) {
              console.error(err);
            }
          })
        );
        setRoomAvailabilities(newAvailabilities);
      };
      fetchAvailability();
    }
  }, [editRoomsList, editCheckInDate, editCheckOutDate, editModalVisible]);

  const isEditValid = React.useMemo(
    () => true,
    [editCheckInDate, editCheckOutDate, editRoomsList],
  );

  const [allocateModalVisible, setAllocateModalVisible] = useState(false);
  const [checkInModalVisible, setCheckInModalVisible] = useState(false);
  const [allocateForm] = Form.useForm();
  const [checkInForm] = Form.useForm();
  const [rooms, setRooms] = useState<any[]>([]);
  const [roomTypes, setRoomTypes] = useState<any[]>([]);
  const [offers, setOffers] = useState<any[]>([]);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const response = statusFilter
        ? await adminBookingService.getBookingsByStatus(statusFilter)
        : await adminBookingService.getAllBookings();

      const rows = Array.isArray(response.data)
        ? (response.data as Booking[])
        : [];
      setBookings(rows);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      message.error("Failed to fetch bookings");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  // Fetch bookings
  useEffect(() => {
    void fetchBookings();

    const fetchRoomsAndTypes = async () => {
      try {
        const [roomsResponse, typesResponse, offersResponse] =
          await Promise.all([
            adminRoomService.getAllRooms(),
            adminRoomService.getAllRoomTypes(),
            offersService.getOffers(),
          ]);
        setRooms(Array.isArray(roomsResponse.data) ? roomsResponse.data : []);
        setRoomTypes(
          Array.isArray(typesResponse.data) ? typesResponse.data : [],
        );
        setOffers(
          Array.isArray(offersResponse.data) ? offersResponse.data : [],
        );
      } catch (error) {
        console.error("Error fetching auxiliary data:", error);
      }
    };
    void fetchRoomsAndTypes();

    const socket = socketService.connect();

    socket.on("booking_created", (newBooking: Booking) => {
      message.info(`New booking created by ${newBooking.guest_name}`);
      void fetchBookings();
    });

    socket.on("booking_updated", () => {
      void fetchBookings();
    });

    return () => {
      socket.off("booking_created");
      socket.off("booking_updated");
    };
  }, [fetchBookings]);

  const handleView = (booking: any) => {
    setSelectedBooking(booking);
    setDrawerVisible(true);
  };

  const handleOpenEdit = useCallback(
    (booking: any) => {
      setEditingBooking(booking);
      
      const existingDiscountAmt = Number(booking.discount_amount || 0);
      const existingDiscountPct = Number(booking.discount_percentage || 0);
      let discountType = "none";
      let discountValue = 0;
      if (existingDiscountPct > 0) {
        discountType = "percentage";
        discountValue = existingDiscountPct;
      } else if (existingDiscountAmt > 0) {
        discountType = "fixed";
        discountValue = existingDiscountAmt;
      }

      const existingServiceChargeAmt = Number(booking.service_charge || 0);
      const existingServiceChargePct = Number(booking.service_charge_percentage || 0);
      let serviceChargeType = "none";
      let serviceChargeValue = 0;
      if (existingServiceChargePct > 0) {
        serviceChargeType = "percentage";
        serviceChargeValue = existingServiceChargePct;
      } else if (existingServiceChargeAmt > 0) {
        serviceChargeType = "fixed";
        serviceChargeValue = existingServiceChargeAmt;
      }

      editForm.setFieldsValue({
        guest_name: booking.guest_name,
        email: booking.email,
        phone: booking.phone,
        address_line_1: booking.address_line_1,
        address_line_2: booking.address_line_2,
        id_document_type: booking.id_document_type,
        id_document_number: booking.id_document_number,
        reservation_source: booking.reservation_source || "advance",
        check_in_date: booking.check_in_date
          ? dayjs(booking.check_in_date)
          : undefined,
        check_out_date: booking.check_out_date
          ? dayjs(booking.check_out_date)
          : undefined,
        rooms: booking.is_group_parent && booking.children && booking.children.length > 0
          ? booking.children.map((child: any) => ({
              room_type_id: child.room_type_id,
              quantity: 1,
              extra_beds: child.extra_beds || 0,
              number_of_guests: child.number_of_guests || 1,
              original_room_price: child.original_room_price,
              discounted_price: child.discounted_price,
              offer_title: child.offer_title,
              ac_status: child.ac_status && child.ac_status !== 'both' ? child.ac_status : undefined,
            }))
          : [
              {
                room_type_id: booking.room_type_id,
                quantity: booking.quantity || 1,
                extra_beds: booking.extra_beds || 0,
                number_of_guests: booking.number_of_guests || 1,
                original_room_price: booking.original_room_price,
                discounted_price: booking.discounted_price,
                offer_title: booking.offer_title,
                ac_status: booking.ac_status && booking.ac_status !== 'both' ? booking.ac_status : undefined,
              },
            ],
        early_check_in_fee: booking.early_check_in_fee || 0,
        late_check_out_fee: booking.late_check_out_fee || 0,
        service_charge_type: serviceChargeType,
        service_charge_value: serviceChargeValue || undefined,
        discount_type: discountType,
        discount_value: discountValue || undefined,
        enable_vat: Number(booking.vat_rate || 0) > 0 ? "yes" : "no",
        vat_percentage: Number(booking.vat_rate || 0) > 0 ? Number(booking.vat_rate) : undefined,
        special_requests: booking.special_requests,
        allocation_note: booking.allocation_note,
      });
      setEditActiveTab("1");
      setEditModalVisible(true);
    },
    [editForm],
  );

  const handleEditSubmit = async (values: any) => {
    if (!editingBooking) return;
    try {
      setLoading(true);

      const checkInFormatted = values.check_in_date
        ? values.check_in_date.format("YYYY-MM-DD")
        : undefined;
      const checkOutFormatted = values.check_out_date
        ? values.check_out_date.format("YYYY-MM-DD")
        : undefined;

      const realId = editingBooking.id.split("-child-")[0];
      const isBulkUpdate =
        (editingBooking.quantity && editingBooking.quantity > 1) ||
        (editingBooking as any).is_group_parent;

      if (isBulkUpdate) {
        const groupPayload: any = {
          guest_name: values.guest_name,
          email: values.email,
          phone: values.phone,
          address_line_1: values.address_line_1,
          address_line_2: values.address_line_2,
          reservation_source: values.reservation_source,
          check_in_date: checkInFormatted,
          check_out_date: checkOutFormatted,
          early_check_in_fee: values.early_check_in_fee || 0,
          late_check_out_fee: values.late_check_out_fee || 0,
          allocation_note: values.allocation_note,
          rooms: values.rooms,
          special_requests: values.special_requests,
          discount_amount: values.discount_type === 'fixed' ? Number(values.discount_value || 0) : 0,
          discount_percentage: values.discount_type === 'percentage' ? Number(values.discount_value || 0) : 0,
          vat_rate: values.enable_vat === 'yes' ? Number(values.vat_percentage || 0) : 0,
          service_charge: values.service_charge_type === 'fixed' ? Number(values.service_charge_value || 0) : 0,
          service_charge_percentage: values.service_charge_type === 'percentage' ? Number(values.service_charge_value || 0) : 0,
          id_document_type: values.id_document_type,
          id_document_number: values.id_document_number,
        };
        await adminBookingService.updateBulkBookingV2(realId, groupPayload);
      } else {
        const payload: any = {
          guest_name: values.guest_name,
          email: values.email,
          phone: values.phone,
          reservation_source: values.reservation_source,
          check_in_date: checkInFormatted,
          check_out_date: checkOutFormatted,
          special_requests: values.special_requests,
          room_type_id: values.rooms?.[0]?.room_type_id,
          number_of_guests: (values.rooms || []).reduce((acc: number, r: any) => acc + (Number(r.number_of_guests) || 1), 0),
          extra_beds: (values.rooms || []).reduce((acc: number, r: any) => acc + (Number(r.extra_beds) || 0), 0),
          quantity: (values.rooms || []).reduce((acc: number, r: any) => acc + (Number(r.quantity) || 1), 0),
          ac_status: values.rooms?.[0]?.ac_status,
          early_check_in_fee: values.early_check_in_fee || 0,
          late_check_out_fee: values.late_check_out_fee || 0,
          allocation_note: values.allocation_note,
          discount_amount: values.discount_type === 'fixed' ? Number(values.discount_value || 0) : 0,
          discount_percentage: values.discount_type === 'percentage' ? Number(values.discount_value || 0) : 0,
          vat_rate: values.enable_vat === 'yes' ? Number(values.vat_percentage || 0) : 0,
          service_charge: values.service_charge_type === 'fixed' ? Number(values.service_charge_value || 0) : 0,
          service_charge_percentage: values.service_charge_type === 'percentage' ? Number(values.service_charge_value || 0) : 0,
          id_document_type: values.id_document_type,
          id_document_number: values.id_document_number,
        };
        await adminBookingService.updateBookingV2(realId, payload);
      }

      message.success("Booking updated successfully");
      setEditModalVisible(false);
      setEditActiveTab("1");
      fetchBookings();
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update booking",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCheckOut = useCallback((booking: any) => {
    setSelectedCheckOutBooking(booking);
    setCheckOutModalVisible(true);
  }, []);

  const handleCheckOutSubmit = async (values: any) => {
    if (!selectedCheckOutBooking) return;
    try {
      setLoading(true);
      const realId = selectedCheckOutBooking.id.split("-child-")[0];
      await frontDeskService.checkOut(realId, {
        payment_method: values.payment_method,
        transaction_id: values.transaction_id,
        settle_balance: values.settle_balance === true,
        allow_outstanding_balance: values.allow_outstanding_balance === true,
        outstanding_reason: values.outstanding_reason || undefined,
      });
      message.success("Check-out successful");
      setCheckOutModalVisible(false);

      // Open inspection modal
      setSelectedRoomCheckBooking(selectedCheckOutBooking);
      setRoomCheckModalVisible(true);

      fetchBookings();
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.message || error?.message || "Failed to check out",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRoomCheck = useCallback((booking: any) => {
    setSelectedRoomCheckBooking(booking);
    setRoomCheckModalVisible(true);
  }, []);

  const handleDelete = (booking: any) => {
    Modal.confirm({
      title: "Delete Booking",
      content: `Are you sure you want to delete the booking for ${booking.guest_name}? This action cannot be undone and will remove it entirely from the system.`,
      okText: "Yes, Delete",
      cancelText: "No",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          setLoading(true);
          const parts = booking.id.split("-child-");
          const realId = parts[0];

          if (parts.length > 1) {
            const childIndex = parseInt(parts[1], 10);
            await adminBookingService.deleteBookingChild(realId, childIndex);
          } else {
            await adminBookingService.deleteBooking(realId);
          }
          message.success("Booking deleted successfully");
          setDrawerVisible(false);
          fetchBookings();
        } catch (error) {
          console.error("Error deleting booking:", error);
          message.error("Failed to delete booking");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleNoShow = (booking: any) => {
    Modal.confirm({
      title: "Mark as No-Show",
      content: `Mark the booking for ${booking.guest_name} as no-show? The room hold is released and the status cannot be changed afterwards.`,
      okText: "Mark No-Show",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          setLoading(true);
          const realId = String(booking.id).split("-child-")[0];
          await adminBookingService.markNoShow(realId);
          message.success("Booking marked as no-show");
          fetchBookings();
        } catch (error: any) {
          console.error("Error marking no-show:", error);
          message.error(
            error?.response?.data?.message || "Failed to mark no-show",
          );
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleOpenAllocate = useCallback(
    (booking: any) => {
      const isChild = booking.is_group_child;
      const isMulti = booking.quantity && booking.quantity > 1 && !isChild;
      const isAllocated = isMulti
        ? booking.allocated_rooms && booking.allocated_rooms.length > 0
        : !!booking.room_id;

      setSelectedBooking(booking);
      allocateForm.setFieldsValue({
        mode: isAllocated ? "manual" : "auto",
        room_id: booking.room_id || undefined,
        room_ids: booking.allocated_rooms
          ? booking.allocated_rooms.map((r: any) => r.id)
          : [],
        note: (booking as any).allocation_note || "",
        ac_selection:
          booking.ac_status === "ac" || booking.ac_status === "non_ac"
            ? booking.ac_status
            : undefined,
      });
      setAllocateModalVisible(true);
    },
    [allocateForm],
  );

  const handleAllocateSubmit = async (values: any) => {
    if (!selectedBooking) return;
    try {
      setLoading(true);
      const isChild = (selectedBooking as any).is_group_child;
      const isMulti =
        selectedBooking.quantity && selectedBooking.quantity > 1 && !isChild;

      let roomIds =
        values.mode === "manual"
          ? isMulti
            ? values.room_ids
            : values.room_id
              ? [values.room_id]
              : []
          : [];

      // If it's a child, we need to merge the new single room_id with the OTHER existing allocations for the parent group.
      if (isChild) {
        const parentAllocatedRooms =
          (selectedBooking as any).allocated_rooms || [];
        const currentChildRoomId = (selectedBooking as any).room_id;
        const newChildRoomId = values.mode === "manual" ? values.room_id : null;

        const existingIds = parentAllocatedRooms.map((r: any) => r.id);

        if (currentChildRoomId) {
          // Replace the old room ID with the new one (if new one exists), else remove it
          roomIds = existingIds
            .map((id: string) =>
              id === currentChildRoomId ? newChildRoomId : id,
            )
            .filter(Boolean);
        } else if (newChildRoomId) {
          // Add the new room ID to the existing ones
          roomIds = [...existingIds, newChildRoomId].filter(Boolean);
        } else {
          // If newChildRoomId is missing and current is missing, keep existing
          roomIds = existingIds;
        }
      }

      const realId = selectedBooking.id.split("-child-")[0];
      await adminBookingService.allocateRooms(realId, {
        roomIds: roomIds || [],
        note: values.note,
      });
      message.success("Room allocation completed");
      setAllocateModalVisible(false);
      fetchBookings();
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to allocate room",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCheckIn = useCallback((booking: any) => {
    setSelectedBooking(booking);
    setCheckInModalVisible(true);
  }, []);

  const handleCheckInSubmit = async (values: any) => {
    if (!selectedBooking) return;
    try {
      setLoading(true);
      const realId = selectedBooking.id.split("-child-")[0];
      await frontDeskService.checkIn(realId, {
        pay_remaining: !!values.pay_remaining,
        payment_method: values.payment_method,
        transaction_id: values.transaction_id,
        room_package_id: selectedBooking.room_package_id ?? undefined,
        // Receipt Type / VAT were removed from the popup — derive them from the
        // booking so check-in still recomputes the total exactly as before.
        receipt_type: Number(selectedBooking.vat_rate || 0) > 0 ? "vat" : "vat_free",
        vat_rate: Number(selectedBooking.vat_rate || 0),
        early_check_in: !!values.early_check_in,
        early_check_in_fee: values.early_check_in
          ? Number(values.early_check_in_fee || 0)
          : 0,
      });
      message.success("Check-in successful");
      setCheckInModalVisible(false);
      fetchBookings();
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to check in",
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePrintReceipt = useCallback(async (bookingId: string, payload: any) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("admin_token");
      const baseUrl =
        process.env.REACT_APP_API_URL || "http://localhost:5000/api/v1";

      const v2BaseUrl = baseUrl.replace('/api/v1', '/api/v2');
      const response = await fetch(`${v2BaseUrl}/bookings/invoice`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to generate receipt PDF");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (error: any) {
      console.error(error);
      message.error("Failed to load receipt PDF");
    } finally {
      setLoading(false);
    }
  }, []);

  const filteredBookings = bookings.filter((booking) => {
    if (booking.reservation_source !== "advance") return false;
    const query = searchText.toLowerCase();
    const matchesSearch =
      booking.guest_name.toLowerCase().includes(query) ||
      booking.email.toLowerCase().includes(query) ||
      (booking.phone || "").includes(searchText) ||
      (booking.room_number || "").toLowerCase().includes(query) ||
      booking.confirmation_number?.includes(searchText);
    if (!matchesSearch) return false;
    if (dateRangeFilter) {
      const rangeStart = dateRangeFilter[0].format("YYYY-MM-DD");
      const rangeEnd = dateRangeFilter[1].format("YYYY-MM-DD");
      const checkIn = dayjs(booking.check_in_date).format("YYYY-MM-DD");
      const checkOut = dayjs(booking.check_out_date).format("YYYY-MM-DD");
      if (!(checkIn <= rangeEnd && checkOut >= rangeStart)) return false;
    }
    return true;
  });

  const groupedBookings = React.useMemo(() => {
    return filteredBookings.map((booking: any) => {
      if (booking.quantity && booking.quantity > 1) {
        const children = [];
        for (let i = 0; i < booking.quantity; i++) {
          const allocatedRoom = booking.allocated_rooms?.[i];
          children.push({
            ...booking,
            id: `${booking.id}-child-${i}`,
            is_group_child: true,
            is_group_parent: false,
            allocated_room_display: allocatedRoom
              ? `${allocatedRoom.room_number} (Floor ${allocatedRoom.floor})`
              : "Not Allocated",
            room_number: allocatedRoom ? allocatedRoom.room_number : null,
            room_id: allocatedRoom ? allocatedRoom.id : null,
            ac_status: booking.ac_status,
            number_of_guests: Math.ceil(
              booking.number_of_guests / booking.quantity,
            ), // Approximate split
            extra_beds: 0, // Simplification
            total_price: Number(booking.total_price) / booking.quantity,
          });
        }
        return {
          ...booking,
          is_group_parent: true,
          children,
        };
      } else {
        const allocatedRoom = booking.allocated_rooms?.[0];
        return {
          ...booking,
          allocated_room_display: allocatedRoom
            ? `${allocatedRoom.room_number} (Floor ${allocatedRoom.floor})`
            : "Not Allocated",
          room_number: allocatedRoom
            ? allocatedRoom.room_number
            : booking.room_number,
          room_id: allocatedRoom ? allocatedRoom.id : booking.room_id,
        };
      }
    });
  }, [filteredBookings]);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: "orange",
      confirmed: "cyan",
      checked_in: "green",
      checked_out: "blue",
      cancelled: "red",
      reserved: "purple",
      blocked: "volcano",
      no_show: "magenta",
    };
    return colors[status] || "default";
  };

  const getPaymentStatusColor = (status?: string) => {
    const colors: Record<string, string> = {
      pending: "orange",
      completed: "green",
      failed: "red",
    };
    return colors[status || "pending"] || "default";
  };

  const bookingCols = React.useMemo(
    () =>
      getBookingColumns({
        handlePrintReceipt,
        handleOpenAllocate,
        handleOpenEdit,
        handleOpenCheckIn,
        handleOpenCheckOut,
        handleDeleteBooking: handleDelete as any,
        handleOpenView: handleView as any,
        handleNoShow,
      }),
    [
      handleOpenAllocate,
      handleOpenEdit,
      handleOpenCheckIn,
      handleOpenCheckOut,
      handleView,
      handlePrintReceipt,
      handleNoShow,
    ],
  );

  return (
    <div className="bookings-container">
      <div className="bookings-header">
        <h1>Bookings Management</h1>
        <p>Manage all guest bookings and reservations</p>
      </div>

      <div className="bookings-filters">
        <Input
          placeholder="Search by name, email, phone, room, or confirmation #"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: "300px" }}
          allowClear
        />
        <Select
          placeholder="Filter by status"
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ width: "200px" }}
          allowClear
          options={[
            { label: "Pending", value: "pending" },
            { label: "Confirmed", value: "confirmed" },
            { label: "Checked In", value: "checked_in" },
            { label: "Checked Out", value: "checked_out" },
            { label: "Cancelled", value: "cancelled" },
            { label: "Reserved", value: "reserved" },
            { label: "Blocked", value: "blocked" },
            { label: "No Show", value: "no_show" },
          ]}
        />
        <RangePicker
          value={dateRangeFilter}
          onChange={(dates) =>
            setDateRangeFilter(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)
          }
        />
        <Button onClick={fetchBookings} loading={loading}>
          Refresh
        </Button>
        <Button icon={<PrinterOutlined />} onClick={() => setPrintModalOpen(true)}>
          Print Reservation List
        </Button>
      </div>

      <Spin spinning={loading}>
        <Table
          columns={bookingCols}
          dataSource={groupedBookings}
          rowKey="id"
          rowClassName={(record: any) => {
            if (record.is_group_parent) return "group-parent-row";
            if (record.is_group_child) return "group-child-row";
            return "";
          }}
          onRow={(record) => ({
            onClick: () => {
              // Only allow viewing the parent booking since children are virtual
              if (!(record as any).is_group_child) {
                handleView(record);
              }
            },
            style: {
              cursor: (record as any).is_group_child ? "default" : "pointer",
            },
          })}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} bookings`,
          }}
          scroll={{ x: 1400 }}
          expandable={{
            expandIconColumnIndex: 8,
            expandIcon: ({ expanded, onExpand, record }) =>
              (record as any).is_group_parent ? (
                <Button
                  size="small"
                  type="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onExpand(record, e as any);
                  }}
                >
                  View Group Booking{" "}
                  {expanded ? <DownOutlined /> : <RightOutlined />}
                </Button>
              ) : null,
          }}
        />
      </Spin>

      {/* View Booking Modal */}
      <ViewBookingModal
        open={drawerVisible}
        onCancel={() => setDrawerVisible(false)}
        booking={selectedBooking as any}
        roomTypes={roomTypes}
        offers={offers}
        onEdit={(row) => handleOpenEdit(row as any)}
        onDelete={(row) => handleDelete(row as any)}
        onRoomCheck={(row) => handleOpenRoomCheck(row as any)}
        onAllocate={(row) => handleOpenAllocate(row as any)}
        onCheckIn={(row) => handleOpenCheckIn(row as any)}
        onPrintReceipt={handlePrintReceipt}
      />

      <AllocateModal
        open={allocateModalVisible}
        onCancel={() => setAllocateModalVisible(false)}
        form={allocateForm}
        onSubmit={handleAllocateSubmit}
        selectedBooking={selectedBooking as any}
        rooms={rooms}
        bookings={groupedBookings as any}
      />

      <CheckInModal
        open={checkInModalVisible}
        onCancel={() => {
          setCheckInModalVisible(false);
          checkInForm.resetFields();
        }}
        form={checkInForm}
        onSubmit={handleCheckInSubmit}
        selectedCheckInBooking={selectedBooking as any}
      />

      <EditBookingModal
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditActiveTab("1");
        }}
        form={editForm}
        activeTab={editActiveTab}
        setActiveTab={setEditActiveTab}
        onSubmit={handleEditSubmit}
        loading={loading}
        isEditValid={isEditValid}
        editValidationErrors={[]}
        selectedEditBooking={editingBooking as any}
        roomTypes={roomTypes}
        roomAvailabilities={roomAvailabilities}
        blockedDates={blockedDates}
        editCheckInDate={editCheckInDate}
        editCheckOutDate={editCheckOutDate}
        editRoomsList={editRoomsList}
        offers={offers}
        rooms={rooms}
      />

      <CheckOutModal
        open={checkOutModalVisible}
        onCancel={() => {
          setCheckOutModalVisible(false);
          checkOutForm.resetFields();
        }}
        form={checkOutForm}
        onSubmit={handleCheckOutSubmit}
        selectedCheckOutBooking={selectedCheckOutBooking as any}
        loading={loading}
      />

      <RoomCheckOutInspectionModal
        open={roomCheckModalVisible}
        onCancel={() => setRoomCheckModalVisible(false)}
        booking={selectedRoomCheckBooking as any}
      />

      <Modal
        title="Print Reservation List"
        open={printModalOpen}
        onCancel={() => setPrintModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setPrintModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="print" type="primary" loading={printLoading} onClick={handlePrintReservationList}>
            Print
          </Button>,
        ]}
        width={420}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <div style={{ marginBottom: 6, fontWeight: 500 }}>Date Range</div>
            <DatePicker.RangePicker
              style={{ width: "100%" }}
              value={[dayjs(printDateFrom), dayjs(printDateTo)]}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setPrintDateFrom(dates[0].format("YYYY-MM-DD"));
                  setPrintDateTo(dates[1].format("YYYY-MM-DD"));
                }
              }}
            />
          </div>
          <div>
            <div style={{ marginBottom: 6, fontWeight: 500 }}>Status Filter</div>
            <Select
              style={{ width: "100%" }}
              placeholder="All statuses"
              allowClear
              options={[
                { label: "Pending", value: "pending" },
                { label: "Confirmed", value: "confirmed" },
                { label: "Checked-In", value: "checked_in" },
                { label: "Checked-Out", value: "checked_out" },
                { label: "Cancelled", value: "cancelled" },
                { label: "Reserved", value: "reserved" },
                { label: "Blocked", value: "blocked" },
                { label: "No Show", value: "no_show" },
              ]}
              value={printStatusFilter}
              onChange={setPrintStatusFilter}
            />
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default Bookings;
